/**
 * RefreshEvidencePanel — popover next to SedStatusPill that lets the
 * user re-fetch SED sections deliberately, instead of relying on the
 * implicit top-up that fires inside generateTypedReport.
 *
 * The site still owns one canonical SED; this panel is a presentation
 * layer over the same topUpSiteSed pipeline, with onlyProviders set
 * to the user's selection.
 *
 * Disabled-state matrix:
 *   - sedStatus 'bootstrapping' → button hidden (initial bootstrap
 *     still running; the SedStatusPill spinner already says so).
 *   - sedStatus 'failed'        → popover trigger flips to a Retry
 *     surface (NS-2 SHV-FOLLOWUP-RETRY-IN-REFRESH). SHV-3 stripped the
 *     SedStatusPill from the toolbar; without this branch the failed
 *     state had NO retry affordance on the toolbar. When the host
 *     doesn't pass `onRetrySed` (legacy callers) the trigger still
 *     hides, preserving the pre-SHV-3 contract.
 *   - report generation in flight → button disabled with tooltip.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { RefreshCw, Loader2, AlertTriangle } from 'lucide-react';
import { useStore } from '../../store';
import {
  computeSedSectionStatuses,
  providersForSections,
  EST_PROVIDER_COST_USD,
  type SedSectionStatus,
} from '../../lib/sedStaleness';
import type { SedSectionId, SiteEvidenceDocument } from '../../types/siteEvidence';
import type { SiteEntity } from '../../types/site';
import { useIsDatacrateAdmin } from '../../lib/isDatacrateAdmin';

interface Props {
  site: SiteEntity;
  /** @deprecated Cost visibility is now gated internally on
   *  `useIsDatacrateAdmin()` (Datacrate admin), not on this prop.
   *  Retained for call-site compatibility; ignored for the cost gate. */
  isAdmin: boolean;
  /**
   * NS-2 (SHV-FOLLOWUP-RETRY-IN-REFRESH) — failed-state retry.
   *
   * SHV-3 stripped DataReadinessPill + SedStatusPill from the
   * SiteEvidenceToolbar (the prior home for the SED-failed Retry CTA),
   * but kept `onRetrySed` on the toolbar's Props interface for host
   * call-site stability. NS-2 re-wires that callback through this
   * panel: when `site.sedStatus === 'failed'` AND `onRetrySed` is
   * provided, the popover trigger flips to a "Retry SED build" surface
   * — single-click CTA that fires the host's retry handler (same
   * `handleRetryBootstrap` path the old pill used). When absent the
   * panel falls back to the pre-NS-2 behaviour (hide trigger on
   * failed), so legacy callers stay back-compat.
   */
  onRetrySed?: () => void;
  /** Controlled open — when provided, the host owns popover visibility. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Hide the inline trigger button (host opens via Actions menu). */
  hideTrigger?: boolean;
}

/** Format a "X days ago" label without a date library — kept plain so
 *  the popover isn't carrying a 200KB date dep for one line of copy. */
function formatAge(ageDays: number | undefined, lastFetchedAt: string | undefined): string {
  if (lastFetchedAt === undefined) return 'never';
  if (ageDays === undefined) return new Date(lastFetchedAt).toLocaleDateString();
  if (ageDays === 0) return 'today';
  if (ageDays === 1) return '1 day ago';
  return `${ageDays} days ago`;
}

const STALENESS_PILL: Record<SedSectionStatus['staleness'], string> = {
  fresh:   'bg-success/10 border-success/40 text-success',
  stale:   'bg-warning/10 border-warning/40 text-warning',
  missing: 'bg-danger/10 border-danger/40 text-danger',
};

export const RefreshEvidencePanel: React.FC<Props> = ({
  site,
  isAdmin: _isAdmin,
  onRetrySed,
  open: openProp,
  onOpenChange,
  hideTrigger = false,
}) => {
  // Cost figures are visible to Datacrate admins only (internal
  // ops telemetry). The legacy `isAdmin` prop (org-role) is ignored.
  const isDatacrateAdmin = useIsDatacrateAdmin();
  const refreshSedSections = useStore(s => s.refreshSedSections);
  const isLoading = useStore(s => s.siteRag.isLoading);
  const progressStage = useStore(s => s.siteRag.progressStage);

  const [openInternal, setOpenInternal] = useState(false);
  const open = openProp ?? openInternal;
  const setOpen = (next: boolean) => {
    onOpenChange?.(next);
    if (openProp === undefined) setOpenInternal(next);
  };
  const [sed, setSed] = useState<SiteEvidenceDocument | null>(null);
  const [sedLoading, setSedLoading] = useState(false);
  const [selected, setSelected] = useState<Set<SedSectionId>>(new Set());

  // Lazy-load the SED only when the popover opens — the staleness
  // calculation needs per-section retrievedAt timestamps.
  useEffect(() => {
    if (!open) return;
    if (!site.sedId) {
      setSed(null);
      return;
    }
    let cancelled = false;
    setSedLoading(true);
    (async () => {
      try {
        const { loadSedById } = await import('../../services/sedLoader');
        const doc = await loadSedById(site.sedId!, site.sedStoragePath);
        if (!cancelled) setSed(doc);
      } catch {
        if (!cancelled) setSed(null);
      } finally {
        if (!cancelled) setSedLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, site.sedId, site.sedStoragePath, site.sedVersion]);

  const statuses = useMemo(() => computeSedSectionStatuses(sed), [sed]);
  // Only sections backed by a live provider are user-refreshable;
  // hide the rest from the popover so the cost/preview math stays
  // honest.
  const refreshableStatuses = useMemo(
    () => statuses.filter(s => s.providers.length > 0),
    [statuses],
  );

  const providersToRun = useMemo(
    () => providersForSections(refreshableStatuses, [...selected]),
    [refreshableStatuses, selected],
  );
  const estimatedCostUsd = providersToRun.length * EST_PROVIDER_COST_USD;

  const reportInFlight = isLoading && (progressStage ?? '').toLowerCase().includes('generating');

  // Disabled-state matrix.
  //   - 'bootstrapping' → trigger hidden (SedRebuildBanner / spinner
  //     in the toolbar already say "wait, this is running").
  //   - 'failed' → NS-2: flip to a Retry surface when the host
  //     provided `onRetrySed`. If absent, fall back to the pre-NS-2
  //     "hide trigger" behaviour so legacy callers stay back-compat.
  if (site.sedStatus === 'bootstrapping') return null;
  if (site.sedStatus === 'failed') {
    if (!onRetrySed || hideTrigger) return null;
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={onRetrySed}
        className="h-7 px-2 type-body uppercase tracking-widest border-danger/40 text-danger hover:bg-danger/10 hover:text-danger"
        data-testid="refresh-evidence-retry-failed"
        aria-label="Retry SED build"
        title="The last SED bootstrap failed. Click to retry — re-runs all providers from scratch."
      >
        <AlertTriangle className="w-3 h-3 mr-1" /> Retry SED
      </Button>
    );
  }

  const triggerDisabled = reportInFlight;
  // SET-Q5 — trigger copy + tooltip update. The Site Evidence toolbar
  // surfaces THREE refresh-like actions. Distinct verbs eliminate the
  // "which button do I click?" ambiguity:
  //   • Reload          — re-fetch cached SED JSON (no provider calls; <1s)
  //   • Re-query providers… — this panel; section-scoped re-query (popover)
  //   • Rebuild SED     — full re-query of all providers (~2–5 min, destructive)
  const triggerTitle = reportInFlight
    ? 'A report is generating — re-query queued after.'
    : 'Re-query providers for selected SED sections (section-scoped; opens a chooser).';

  const setStaleOnly = () => {
    setSelected(new Set(refreshableStatuses
      .filter(s => s.staleness === 'stale' || s.staleness === 'missing')
      .map(s => s.id)));
  };
  const setAll = () => setSelected(new Set(refreshableStatuses.map(s => s.id)));
  const setCustom = () => { /* Custom = whatever's currently checked. No-op trigger. */ };

  const toggle = (id: SedSectionId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runRefresh = async () => {
    if (selected.size === 0) return;
    const sectionIds = [...selected];
    setOpen(false);
    setSelected(new Set());
    await refreshSedSections(site.id, sectionIds);
  };

  return (
    <Popover open={open} onOpenChange={(next) => setOpen(typeof next === 'boolean' ? next : Boolean((next as { open?: boolean }).open))}>
      {!hideTrigger && (
        <PopoverTrigger
          render={
            <Button
              variant="outline"
              size="sm"
              disabled={triggerDisabled}
              title={triggerTitle}
              className="h-7 px-2 type-body uppercase tracking-widest"
              data-testid="refresh-evidence-trigger"
              aria-label="Re-query providers"
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Re-query providers…
            </Button>
          }
        />
      )}
      {hideTrigger && (
        <PopoverTrigger
          nativeButton={false}
          render={
            <span
              className="sr-only"
              data-testid="refresh-evidence-trigger-hidden"
              aria-hidden
            />
          }
        />
      )}
      <PopoverContent
        align="start"
        className="w-96 max-h-[70vh] overflow-y-auto"
        data-testid="refresh-evidence-popover"
      >
        <div className="space-y-3">
          <div className="space-y-1">
            {/* SET-Q5 — vocabulary unified across the Reload / Re-query / Rebuild ladder. */}
            <h3 className="type-section text-text-secondary">
              Re-query providers
            </h3>
            <p className="type-body text-text-muted">
              Re-fetch one or more sections of the canonical SED. The site still owns
              a single SED; only the providers you pick will run.
            </p>
          </div>

          <div className="flex items-center gap-1 flex-wrap">
            <Button
              variant="outline" size="sm"
              className="h-6 type-meta uppercase tracking-widest"
              onClick={setStaleOnly}
              data-testid="refresh-stale-only"
            >
              Refresh stale only
            </Button>
            <Button
              variant="outline" size="sm"
              className="h-6 type-meta uppercase tracking-widest"
              onClick={setAll}
              data-testid="refresh-all"
            >
              Refresh all
            </Button>
            <Button
              variant="ghost" size="sm"
              className="h-6 type-meta uppercase tracking-widest"
              onClick={setCustom}
              data-testid="refresh-custom"
              title="Pick sections individually below"
            >
              Custom
            </Button>
          </div>

          {sedLoading ? (
            <div className="type-body text-text-muted flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading evidence…
            </div>
          ) : (
            <ul className="space-y-1">
              {refreshableStatuses.map(s => (
                <li
                  key={s.id}
                  data-testid={`refresh-row-${s.id}`}
                  data-staleness={s.staleness}
                  className="flex items-start gap-2 px-1.5 py-1 hover:bg-muted"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(s.id)}
                    onChange={() => toggle(s.id)}
                    className="mt-0.5 shrink-0"
                    data-testid={`refresh-checkbox-${s.id}`}
                  />
                  <div className="flex-1 min-w-0 text-[10px]">
                    <div className="font-semibold text-text-primary">{s.label}</div>
                    <div className="type-body text-text-muted">
                      Last fetched: {formatAge(s.ageDays, s.lastFetchedAt)} · TTL {s.ttlDays}d
                    </div>
                  </div>
                  <span
                    className={`type-meta px-1 py-0.5 border uppercase tracking-widest ${STALENESS_PILL[s.staleness]}`}
                  >
                    {s.staleness}
                  </span>
                </li>
              ))}
            </ul>
          )}

          <div className="border-t border-border pt-2 space-y-2">
            <div className="type-body text-text-muted flex items-center justify-between">
              <span>{selected.size} section{selected.size === 1 ? '' : 's'} selected</span>
              <span data-testid="refresh-cost-preview">
                {isDatacrateAdmin && <>Est. cost: ${estimatedCostUsd.toFixed(2)} · </>}
                {providersToRun.length} provider{providersToRun.length === 1 ? '' : 's'}
              </span>
            </div>
            <Button
              size="sm"
              className="w-full h-8 type-body uppercase tracking-widest"
              disabled={selected.size === 0 || providersToRun.length === 0}
              onClick={() => void runRefresh()}
              data-testid="refresh-run"
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Run refresh
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
